import { required, useRecordContext } from "ra-core";
import { Link } from "react-router";
import { Plus } from "lucide-react";
import { BooleanInput } from "@/components/admin/boolean-input";
import { Create } from "@/components/admin/create";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { DeleteButton } from "@/components/admin/delete-button";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { ReferenceManyCount } from "@/components/admin/reference-many-count";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextField } from "@/components/admin/text-field";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MobilePage } from "../MobilePage";
import { useIsDueno } from "../useIsDueno";

const CHANNELS = [
  { id: "llamada", name: "Llamada" },
  { id: "whatsapp", name: "WhatsApp" },
  { id: "email", name: "Correo" },
];
const channelName = (c: string) => CHANNELS.find((x) => x.id === c)?.name ?? c;

const OfferInput = () => (
  <ReferenceInput
    source="offer_id"
    reference="offers"
    sort={{ field: "priority", order: "ASC" }}
  >
    <SelectInput label="Oferta" optionText="name" validate={required()} />
  </ReferenceInput>
);

export const SequenceList = () => {
  // Raftel's catalog is read-only for freelancers (E6.4): only the owner creates or edits
  const isDueno = useIsDueno();
  return (
    <MobilePage title="Cadencias">
      <List
        title="Cadencias"
        exporter={false}
        sort={{ field: "offer_id", order: "ASC" }}
        disableBreadcrumb
        actions={isDueno ? <CreateButton label="Nueva cadencia" /> : false}
      >
        <p className="text-sm text-muted-foreground mb-2">
          Una cadencia es una serie de pasos (llamar, WhatsApp, correo) en días
          fijos. Al inscribir a alguien se crean sus tareas en Mi día; si
          responde, agenda demo o pide baja, las tareas pendientes se borran.
        </p>
        <DataTable
          bulkActionButtons={false}
          rowClick={isDueno ? "edit" : false}
        >
          <DataTable.Col label="Oferta" source="offer_id">
            <ReferenceField source="offer_id" reference="offers" link={false}>
              <TextField source="name" className="font-medium" />
            </ReferenceField>
          </DataTable.Col>
          <DataTable.Col
            label="Nombre"
            source="name"
            render={(r) => (
              <span>
                {r.name}{" "}
                {!r.active && <Badge variant="outline">inactiva</Badge>}
              </span>
            )}
          />
          <DataTable.Col label="Pasos" disableSort>
            <ReferenceManyCount
              reference="sequence_steps"
              target="sequence_id"
            />
          </DataTable.Col>
        </DataTable>
      </List>
    </MobilePage>
  );
};

export const SequenceCreate = () => (
  <MobilePage title="Nueva cadencia">
    <Create title="Nueva cadencia" redirect="edit">
      <SimpleForm>
        <OfferInput />
        <TextInput source="name" label="Nombre" validate={required()} />
        <BooleanInput source="active" label="Activa" defaultValue={true} />
      </SimpleForm>
    </Create>
  </MobilePage>
);

export const SequenceEdit = () => (
  <MobilePage title="Editar cadencia">
    <Edit title="Editar cadencia" redirect="list" mutationMode="pessimistic">
      <SimpleForm>
        <OfferInput />
        <TextInput source="name" label="Nombre" validate={required()} />
        <BooleanInput source="active" label="Activa" />
        <Steps />
      </SimpleForm>
    </Edit>
  </MobilePage>
);

/** Pasos de la cadencia (se editan en su propia pantalla). */
const Steps = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-medium">Pasos</h3>
      <ReferenceManyField
        reference="sequence_steps"
        target="sequence_id"
        sort={{ field: "step_no", order: "ASC" }}
        perPage={100}
      >
        <DataTable bulkActionButtons={false}>
          <DataTable.Col label="#" source="step_no" />
          <DataTable.Col
            label="Día"
            source="day_offset"
            render={(r) => `Día ${r.day_offset}`}
          />
          <DataTable.Col
            label="Canal"
            source="channel"
            render={(r) => channelName(r.channel)}
          />
          <DataTable.Col label="Plantilla" source="template_id">
            <ReferenceField
              source="template_id"
              reference="templates"
              link={false}
              empty={<span className="text-muted-foreground">—</span>}
            >
              <TextField source="name" />
            </ReferenceField>
          </DataTable.Col>
        </DataTable>
      </ReferenceManyField>
      <Button asChild variant="outline" className="self-start">
        <Link
          to={`/sequence_steps/create?source=${encodeURIComponent(
            JSON.stringify({ sequence_id: record.id }),
          )}`}
        >
          <Plus /> Agregar paso
        </Link>
      </Button>
    </div>
  );
};

const StepForm = () => (
  <SimpleForm>
    <NumberInput
      source="step_no"
      label="Número de paso"
      validate={required()}
      helperText="Orden dentro de la cadencia (1, 2, 3…)"
    />
    <NumberInput
      source="day_offset"
      label="Día"
      validate={required()}
      defaultValue={0}
      helperText="Días después de inscribir (0 = el mismo día)"
    />
    <SelectInput
      source="channel"
      label="Canal"
      choices={CHANNELS}
      validate={required()}
    />
    <ReferenceInput source="template_id" reference="templates">
      <SelectInput
        label="Plantilla"
        optionText={(t) => `${t.name} (${channelName(t.channel)})`}
        emptyText="Sin plantilla"
      />
    </ReferenceInput>
    <TextInput
      source="instructions"
      label="Instrucciones"
      helperText="Texto de la tarea en Mi día (opcional)"
    />
  </SimpleForm>
);

const backToSequence = (
  _: unknown,
  __: unknown,
  data?: Record<string, unknown>,
) => `sequences/${data?.sequence_id}`;

export const SequenceStepCreate = () => (
  <MobilePage title="Nuevo paso">
    <Create title="Nuevo paso" redirect={backToSequence}>
      <StepForm />
    </Create>
  </MobilePage>
);

// Al borrar un paso, volver a su cadencia (no hay lista de pasos suelta)
const StepDeleteButton = () => {
  const record = useRecordContext();
  return (
    <div className="flex justify-end">
      <DeleteButton redirect={`/sequences/${record?.sequence_id}`} />
    </div>
  );
};

export const SequenceStepEdit = () => (
  <MobilePage title="Editar paso">
    <Edit
      actions={<StepDeleteButton />}
      title="Editar paso"
      redirect={backToSequence}
      mutationMode="pessimistic"
    >
      <StepForm />
    </Edit>
  </MobilePage>
);
